import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { IncludedMethod } from "./types";

type Props = {
    readonly methods: ReadonlyArray<IncludedMethod>;
};

const CHIP_GAP = 4;

const IncludedMethodsCell = ({ methods }: Props) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = useState(methods.length);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        if (!container || !measure) return;

        const compute = () => {
            const containerWidth = container.clientWidth;
            const chips = Array.from(
                measure.querySelectorAll<HTMLElement>('[data-chip="method"]')
            );
            const overflowChip = measure.querySelector<HTMLElement>('[data-chip="overflow"]');
            const overflowWidth = (overflowChip?.offsetWidth ?? 0) + CHIP_GAP;

            let used = 0;
            let count = 0;
            for (let i = 0; i < chips.length; i += 1) {
                const chip = chips[i];
                const chipWidth = chip.offsetWidth + (i === 0 ? 0 : CHIP_GAP);
                const remainingAfter = chips.length - (i + 1);
                const widthIfFitWithOverflow = used + chipWidth
                    + (remainingAfter > 0 ? overflowWidth : 0);

                if (widthIfFitWithOverflow <= containerWidth) {
                    used += chipWidth;
                    count = i + 1;
                } else {
                    break;
                }
            }

            // Edge case: if nothing fits but there's at least one method, force one chip
            if (count === 0 && chips.length > 0 && chips[0].offsetWidth <= containerWidth) {
                count = 1;
            }

            setVisibleCount(count);
        };

        compute();

        const observer = new ResizeObserver(compute);
        observer.observe(container);
        return () => observer.disconnect();
    }, [methods]);

    useEffect(() => {
        setVisibleCount(methods.length);
    }, [methods]);

    if (methods.length === 0) {
        return (
            <Typography variant='body2' color='text.secondary'>
                —
            </Typography>
        );
    }

    const visible = methods.slice(0, visibleCount);
    const hiddenCount = methods.length - visibleCount;
    const tooltipContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {methods.map(m => (
                <Typography key={m.id} variant='body2' sx={{ whiteSpace: 'nowrap' }}>
                    {m.name}
                </Typography>
            ))}
        </Box>
    );

    return (
        <Tooltip title={tooltipContent} arrow placement='top'>
            <Box
                ref={containerRef}
                sx={{
                    position: 'relative',
                    display: 'flex',
                    flexWrap: 'nowrap',
                    overflow: 'hidden',
                    gap: `${CHIP_GAP}px`,
                    minWidth: 0,
                    width: '100%'
                }}
            >
                {/* Hidden measurement layer */}
                <Box
                    ref={measureRef}
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        visibility: 'hidden',
                        display: 'flex',
                        gap: `${CHIP_GAP}px`,
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {methods.map(m => (
                        <Chip
                            key={m.id}
                            label={m.name}
                            size='small'
                            data-chip='method'
                        />
                    ))}
                    <Chip
                        label={`+${methods.length}`}
                        size='small'
                        data-chip='overflow'
                    />
                </Box>
                {/* Visible chips */}
                {visible.map(m => (
                    <Chip
                        key={m.id}
                        label={m.name}
                        size='small'
                        sx={{ flexShrink: 0 }}
                    />
                ))}
                {hiddenCount > 0 && (
                    <Chip
                        label={`+${hiddenCount}`}
                        size='small'
                        color='primary'
                        sx={{ flexShrink: 0, fontWeight: 600 }}
                    />
                )}
            </Box>
        </Tooltip>
    );
};

export default memo(IncludedMethodsCell);
